
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { CourseAnalyticsItem } from "./types";

const CourseFeedbackAnalytics = () => {
  const { data: courseFeedback, isLoading } = useQuery({
    queryKey: ["courseFeedbackAnalytics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_registrations")
        .select(`
          id,
          overall_rating,
          recommend_rating,
          courses (
            id,
            title,
            category
          )
        `)
        .not("feedback", "is", null);

      if (error) throw error;
      
      // Group feedback by course
      const courseGroups: Record<string, CourseAnalyticsItem> = {};
      
      data.forEach(item => {
        const courseId = item.courses?.id;
        const courseTitle = item.courses?.title || "Unknown Course";
        
        if (!courseId) return;
        
        if (!courseGroups[courseId]) {
          courseGroups[courseId] = {
            id: courseId,
            title: courseTitle,
            category: item.courses?.category || "Uncategorized",
            feedbackCount: 0,
            avgRating: 0,
            totalRating: 0,
            ratings: [],
            recommends: []
          };
        }
        
        // Only count ratings that exist
        if (item.overall_rating) {
          courseGroups[courseId].feedbackCount++;
          courseGroups[courseId].totalRating += item.overall_rating;
          courseGroups[courseId].ratings.push(item.overall_rating);
        }
        
        if (item.recommend_rating) {
          courseGroups[courseId].recommends.push(item.recommend_rating);
        }
      });
      
      // Calculate averages
      Object.keys(courseGroups).forEach(key => {
        if (courseGroups[key].feedbackCount > 0) {
          courseGroups[key].avgRating = courseGroups[key].totalRating / courseGroups[key].feedbackCount;
        }
      });
      
      return Object.values(courseGroups);
    },
  });
  
  // Colors for charts
  const COLORS = ['#004161', '#cfb9a2', '#0f8f8c', '#b1c3cf', '#f1eceb'];
  
  // Top rated courses data
  const topRatedCourses = React.useMemo(() => {
    if (!courseFeedback) return [];
    
    return courseFeedback
      .filter(course => course.feedbackCount >= 3) // Only include courses with at least 3 feedback entries
      .sort((a, b) => b.avgRating - a.avgRating)
      .slice(0, 5)
      .map(course => ({
        name: course.title,
        rating: parseFloat(course.avgRating.toFixed(1))
      }));
  }, [courseFeedback]);
  
  // Course category distribution
  const categoryDistribution = React.useMemo(() => {
    if (!courseFeedback) return [];
    
    const categories: Record<string, { name: string; value: number }> = {};
    
    courseFeedback.forEach(course => {
      const category = course.category || "Uncategorized";
      
      if (!categories[category]) {
        categories[category] = {
          name: category,
          value: 0
        };
      }
      
      categories[category].value++;
    });
    
    return Object.values(categories);
  }, [courseFeedback]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <p className="text-muted-foreground">Loading course analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-primary">Top Rated Courses</CardTitle>
            <CardDescription className="font-subtitle">
              Courses with highest average ratings
            </CardDescription>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topRatedCourses}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  domain={[0, 4]} 
                  allowDecimals={true}
                  allowDataOverflow={false}
                  scale="auto"
                  padding={{ left: 0, right: 0 }}
                />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  tick={{ fontSize: 12 }}
                  width={80}
                  padding={{ top: 0, bottom: 0 }}
                  allowDecimals={true}
                  allowDataOverflow={false}
                  scale="auto"
                  yAxisId={0}
                />
                <Tooltip formatter={(value) => [`${value}/4`, 'Average Rating']} />
                <Bar 
                  dataKey="rating" 
                  fill="#0f8f8c"
                  label={{ position: 'right', formatter: (value) => `${value}/4` }}
                >
                  {topRatedCourses.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-primary">Course Categories</CardTitle>
            <CardDescription className="font-subtitle">
              Distribution of courses by category
            </CardDescription>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} courses`, '']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-primary">Course Feedback Overview</CardTitle>
          <CardDescription className="font-subtitle">
            Detailed analysis of all courses with feedback
          </CardDescription>
        </CardHeader>
        <CardContent>
          {courseFeedback && courseFeedback.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2 font-section">Course</th>
                    <th className="text-left p-2 font-section">Category</th>
                    <th className="text-left p-2 font-section">Feedback Count</th>
                    <th className="text-left p-2 font-section">Avg. Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {courseFeedback.map(course => (
                    <tr key={course.id} className="border-b hover:bg-muted/50">
                      <td className="p-2 font-body">{course.title}</td>
                      <td className="p-2 font-body">{course.category}</td>
                      <td className="p-2 font-body">{course.feedbackCount}</td>
                      <td className="p-2 font-body">
                        {course.feedbackCount > 0 ? 
                          <div className="flex items-center">
                            <span className="mr-2">{course.avgRating.toFixed(1)}/4</span>
                            <div className="flex">
                              {[...Array(4)].map((_, i) => (
                                <span 
                                  key={i} 
                                  className={`text-xs ${i < Math.round(course.avgRating) ? 'text-amber-400' : 'text-gray-300'}`}
                                >
                                  ★
                                </span>
                              ))}
                            </div>
                          </div> : 
                          'No ratings'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">
              No course feedback data available
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CourseFeedbackAnalytics;
